/**
 * Hook for canvas zoom and pan management
 * Extracted from Canvas.tsx lines 289-499
 * Coordinates with CanvasViewportContext for zoom/pan state
 */

import React, { type RefObject, useEffect, useRef } from 'react'
import { useCanvasViewport } from '@/contexts/CanvasViewportContext.tsx'
import { MAX_ZOOM, MIN_ZOOM, ZOOM_SENSITIVITY } from '@/constants'
import type { CanvasDimensions, CanvasZoomPanHandlers } from '../BaseCanvas.types'

interface UseCanvasZoomPanOptions {
	whiteboardRef: RefObject<HTMLDivElement | null>
	canvasDimensions: CanvasDimensions
}

/**
 * Provides zoom and pan handlers that coordinate with CanvasViewportContext
 * Supports:
 * - Mouse wheel zoom (cursor-centered)
 * - Spacebar + drag pan
 * - Middle mouse button pan
 * - Automatic pan clamping to keep content in view
 */
export function useCanvasZoomPan({
									 whiteboardRef,
									 canvasDimensions,
								 }: UseCanvasZoomPanOptions): CanvasZoomPanHandlers {
	const { zoom, panX, panY, isPanning, panMode, setViewport } = useCanvasViewport()
	const panStartRef = useRef<{ x: number; y: number } | null>(null)
	const panOriginRef = useRef<{ x: number; y: number } | null>(null)

	// Helper: Calculate cursor-centered zoom transformation
	function calculateCursorCenteredZoom(
		cursorX: number,
		cursorY: number,
		oldZoom: number,
		newZoom: number,
		oldPanX: number,
		oldPanY: number,
	): { newPanX: number; newPanY: number } {
		// Point under cursor in canvas space
		const canvasX = (cursorX - oldPanX) / oldZoom
		const canvasY = (cursorY - oldPanY) / oldZoom

		// New pan to keep that point under cursor
		const newPanX = cursorX - canvasX * newZoom
		const newPanY = cursorY - canvasY * newZoom

		return { newPanX, newPanY }
	}

	// Helper: Clamp pan to keep content within bounds
	function clampPan(
		newPanX: number,
		newPanY: number,
		currentZoom: number,
	): { panX: number; panY: number } {
		const containerWidth = canvasDimensions.width
		const containerHeight = canvasDimensions.height

		// Content size at current zoom
		const contentWidth = containerWidth * currentZoom
		const contentHeight = containerHeight * currentZoom

		// Maximum pan is when content edge reaches container edge
		const maxPanX = 0 // Left edge cannot go past container left
		const minPanX = containerWidth - contentWidth // Right edge cannot go past container right

		const maxPanY = 0
		const minPanY = containerHeight - contentHeight

		return {
			panX: Math.max(minPanX, Math.min(maxPanX, newPanX)),
			panY: Math.max(minPanY, Math.min(maxPanY, newPanY)),
		}
	}

	// Mouse wheel handler for zooming
	function handleWheel(event: WheelEvent) {
		if (!whiteboardRef.current) return

		event.preventDefault()

		// Get cursor position relative to container
		const rect = whiteboardRef.current.getBoundingClientRect()
		const cursorX = event.clientX - rect.left
		const cursorY = event.clientY - rect.top

		// Calculate zoom delta (positive = zoom in, negative = zoom out)
		const delta = -event.deltaY * ZOOM_SENSITIVITY

		// Calculate new zoom, clamped to bounds
		const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom + delta))

		// Only zoom IN (ignore zoom out past baseline)
		if (newZoom < MIN_ZOOM) return

		// Calculate cursor-centered zoom
		const { newPanX, newPanY } = calculateCursorCenteredZoom(
			cursorX,
			cursorY,
			zoom,
			newZoom,
			panX,
			panY,
		)

		// Clamp pan to bounds
		const clamped = clampPan(newPanX, newPanY, newZoom)

		// Reset pan when returning to baseline
		if (newZoom === MIN_ZOOM) {
			setViewport({ zoom: MIN_ZOOM, panX: 0, panY: 0 })
		} else {
			setViewport({ zoom: newZoom, panX: clamped.panX, panY: clamped.panY })
		}
	}

	// Mouse down handler for starting pan
	function handleMouseDown(event: React.MouseEvent<HTMLDivElement>) {
		// Spacebar pan start (left-click while spacebar held)
		if (panMode === 'spacebar' && event.button === 0) {
			event.preventDefault()
			setViewport({ isPanning: true })
			panStartRef.current = { x: event.clientX, y: event.clientY }
			panOriginRef.current = { x: panX, y: panY }
			return
		}

		// Middle mouse button = button 1
		if (event.button === 1 && zoom > MIN_ZOOM) {
			event.preventDefault()
			setViewport({
				isPanning: true,
				panMode: 'middleMouse',
			})
			panStartRef.current = { x: event.clientX, y: event.clientY }
			panOriginRef.current = { x: panX, y: panY }
		}
	}

	// Mouse up handler for ending pan
	function handleMouseUp() {
		if (isPanning) {
			setViewport({
				isPanning: false,
				panMode: null,
			})
			panStartRef.current = null
			panOriginRef.current = null
		}
	}

	// Mouse move handler for active panning
	function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
		// Handle panning
		if (isPanning && panStartRef.current && panOriginRef.current) {
			const deltaX = event.clientX - panStartRef.current.x
			const deltaY = event.clientY - panStartRef.current.y

			// 1:1 mouse tracking - no zoom division
			// panX maps directly to screen pixels due to CSS transform math
			const newPanX = panOriginRef.current.x + deltaX
			const newPanY = panOriginRef.current.y + deltaY

			const clamped = clampPan(newPanX, newPanY, zoom)
			setViewport({ panX: clamped.panX, panY: clamped.panY })
		}
	}

	// Attach wheel event listener for zoom (non-passive to allow preventDefault)
	useEffect(() => {
		const element = whiteboardRef.current
		if (!element) return

		element.addEventListener('wheel', handleWheel, { passive: false })
		return () => element.removeEventListener('wheel', handleWheel)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoom, panX, panY, canvasDimensions])

	// Keyboard event listeners for spacebar pan mode
	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.code === 'Space' && !event.repeat && zoom > MIN_ZOOM && !isPanning) {
				event.preventDefault()
				// Don't set isPanning here - that happens on mousedown
				setViewport({
					panMode: 'spacebar',
				})
			}
		}

		function handleKeyUp(event: KeyboardEvent) {
			if (event.code === 'Space' && panMode === 'spacebar') {
				event.preventDefault()
				setViewport({
					isPanning: false,
					panMode: null,
				})
				panStartRef.current = null
				panOriginRef.current = null
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		document.addEventListener('keyup', handleKeyUp)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('keyup', handleKeyUp)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoom, panMode, isPanning])

	return {
		handleWheel,
		handleMouseDown,
		handleMouseUp,
		handleMouseMove,
		panStartRef,
		panOriginRef,
	}
}
